"use strict";
"use client";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const material_1 = require("@mui/material");
const AddPhotoAlternate_1 = __importDefault(require("@mui/icons-material/AddPhotoAlternate"));
const icons_1 = require("../icons");
const useFileUploader_1 = require("../../hooks/useFileUploader");
const useSimpleCodeGeneration_1 = require("../../../hooks/useSimpleCodeGeneration");
const PictureAsPdf_1 = __importDefault(require("@mui/icons-material/PictureAsPdf"));
const Slideshow_1 = __importDefault(require("@mui/icons-material/Slideshow"));
const Description_1 = __importDefault(require("@mui/icons-material/Description"));
const InsertDriveFile_1 = __importDefault(require("@mui/icons-material/InsertDriveFile"));
const Article_1 = __importDefault(require("@mui/icons-material/Article"));
const SessionBuilderContext_1 = require("../../../context/SessionBuilderContext");
const SelectionContext_1 = require("../../../context/SelectionContext");
const Crop_1 = __importDefault(require("@mui/icons-material/Crop"));
const SessionContext_1 = require("../../../context/SessionContext");
const hooks_1 = require("../../../hooks");
const PolishPrompt_1 = __importDefault(require("./PolishPrompt"));
const config_1 = require("../../../config");
const ChatInput = ({ userId, onCodeGenerated, onError, onStatusUpdate, placeholder = "Write a prompt...", disabled = false, onPromptSend, buildPrompt, }) => {
    const { session } = (0, SessionContext_1.useSession)();
    const { conversation } = (0, SessionBuilderContext_1.useSessionBuilder)();
    const { uploadFile, loading: uploadLoading } = (0, useFileUploader_1.useFileUploader)();
    const { generateCodeFromInput, isGenerating, currentCode, status, cancelGeneration, } = (0, useSimpleCodeGeneration_1.useSimpleCodeGeneration)({
        userId,
        onCodeGenerated,
        onError,
        onStatusUpdate,
        conversation,
    });
    const { showNotification } = (0, hooks_1.useNotifier)();
    const [text, setText] = (0, react_1.useState)("");
    const [imagePreviewUrls, setImagePreviewUrls] = (0, react_1.useState)([]);
    const [uploadedFilesInfo, setUploadedFilesInfo] = (0, react_1.useState)([]);
    const [dragActive, setDragActive] = (0, react_1.useState)(false);
    const { isSelectionMode, setIsSelectionMode, selectedHtml } = (0, SelectionContext_1.useSelection)();
    const ACCEPTED_TYPES = {
        // Images
        "image/png": [".png"],
        "image/jpeg": [".jpeg"],
        "image/jpg": [".jpg"],
        // Documents
        "application/pdf": [".pdf"],
        "application/vnd.ms-powerpoint": [".ppt"],
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    };
    const getFilePreviewIcon = (mimetype) => {
        const type = mimetype.toLowerCase();
        if (type.includes("pdf")) {
            return (0, jsx_runtime_1.jsx)(PictureAsPdf_1.default, { fontSize: "large", color: "error" });
        }
        if (type.includes("presentation") ||
            type.includes("powerpoint") ||
            type.includes("ppt") ||
            type.includes("pptx")) {
            return (0, jsx_runtime_1.jsx)(Slideshow_1.default, { fontSize: "large", color: "warning" });
        }
        if (type.includes("msword") ||
            type.includes("word") ||
            type.includes("doc") ||
            type.includes("docx")) {
            return (0, jsx_runtime_1.jsx)(Description_1.default, { fontSize: "large", color: "primary" });
        }
        // For plain text, markdown, or similar
        if (type.includes("text") || type.includes("markdown")) {
            return (0, jsx_runtime_1.jsx)(Article_1.default, { fontSize: "large", color: "action" });
        }
        // Fallback for any other non-image file
        return (0, jsx_runtime_1.jsx)(InsertDriveFile_1.default, { fontSize: "large", color: "disabled" });
    };
    const isValidImageType = (file) => {
        return Object.keys(ACCEPTED_TYPES).includes(file.type);
    };
    const handleTextSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!text.trim()) {
            console.log("⚠️ No text entered");
            showNotification("Please enter a prompt", "warning");
            return;
        }
        const updatedText = buildPrompt(text.trim(), { selectedHtml });
        console.log("@updatedText", updatedText);
        let attachments = [];
        if (uploadedFilesInfo.length > 0) {
            attachments = uploadedFilesInfo.map((fileInfo) => {
                const isImage = fileInfo.mimetype.startsWith("image/");
                return {
                    url: fileInfo.url,
                    type: isImage ? "image_url" : "file_url",
                };
            });
        }
        const payload = {
            text: updatedText,
            isSelectionMode: !!selectedHtml,
            attachments,
        };
        console.log("🚀 Final payload to generateCodeFromInput:", payload);
        try {
            yield generateCodeFromInput(payload);
        }
        catch (error) {
            console.error("❌ Error generating code:", error);
            showNotification("Something went wrong while processing attachments.", "error");
        }
        if (typeof onPromptSend === "function") {
            onPromptSend(text);
        }
        // Reset fields
        setText("");
        setImagePreviewUrls([]);
        setUploadedFilesInfo([]);
    });
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleTextSubmit();
        }
    };
    const handleImageUpload = (file) => __awaiter(void 0, void 0, void 0, function* () {
        if (!file)
            return;
        const MAX_FILE_SIZE = 1024 * 1024 * 100; // 100MB
        if (!isValidImageType(file)) {
            showNotification("Only PNG, JPEG, DOC, DOCX, PPT, PDF and JPG files are allowed.", "error");
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            showNotification("File size should be less than 20MB.", "error");
            return;
        }
        const reader = new FileReader();
        // 1️⃣ Create preview with blur first
        reader.onloadend = () => {
            setImagePreviewUrls((prev) => [
                ...prev,
                { url: isValidImageType(file) ? reader.result : file.name, uploading: true, fileType: file.type },
            ]);
        };
        reader.readAsDataURL(file);
        try {
            // 2️⃣ Upload the file
            const response = yield uploadFile({
                file,
                module: "email-templates",
                permission: "public",
                uploadType: "image",
            });
            if ((response === null || response === void 0 ? void 0 : response.file_id) || (response === null || response === void 0 ? void 0 : response.id)) {
                const uploadedImageUrl = (response === null || response === void 0 ? void 0 : response.id) ? `${config_1.API_BASE_URL}/v1/files/read/${response.id}` : response === null || response === void 0 ? void 0 : response.file_id;
                const fileInfo = {
                    url: uploadedImageUrl,
                    fileType: response.fileType || file.type.replace(/^image\//, "."),
                    mimetype: response.mimetype || file.type,
                    filename: response.filename || file.name,
                };
                setUploadedFilesInfo((prev) => [...prev, fileInfo]);
                showNotification(`${file.name} uploaded successfully!`, "success");
                // 3️⃣ Remove blur on the corresponding preview
                setImagePreviewUrls((prev) => prev.map((item) => item.url === reader.result
                    ? Object.assign(Object.assign({}, item), { uploading: false }) : item));
            }
            else {
                showNotification(`Failed to upload ${file.name}`, "error");
            }
        }
        catch (error) {
            console.error("Upload failed", error);
            showNotification(`Upload failed for ${file.name}`, "error");
        }
    });
    const handleFileInputChange = (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const files = Array.from((_a = e.target.files) !== null && _a !== void 0 ? _a : []);
        for (const file of files) {
            yield handleImageUpload(file);
        }
    });
    const handleDrop = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files);
        // Sequential
        for (const file of files) {
            yield handleImageUpload(file);
        }
    });
    const isLoading = uploadLoading || isGenerating;
    const isSubmitDisabled = !text.trim() || isLoading || disabled;
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
            border: dragActive ? "2px dashed #1976d2" : "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: dragActive ? "#f5f5f5" : "white",
            transition: "all 0.2s ease",
            // minHeight: "150px",
            opacity: disabled ? 0.6 : 1,
        }, component: "form", onSubmit: (e) => e.preventDefault(), onDrop: handleDrop, onDragOver: (e) => {
            e.preventDefault();
            setDragActive(true);
        }, onDragLeave: () => setDragActive(false), children: [(imagePreviewUrls === null || imagePreviewUrls === void 0 ? void 0 : imagePreviewUrls.length) > 0 && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    marginBottom: "12px",
                    padding: "4px 0",
                    "&::-webkit-scrollbar": {
                        height: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#ccc",
                        borderRadius: "4px",
                    },
                }, children: imagePreviewUrls === null || imagePreviewUrls === void 0 ? void 0 : imagePreviewUrls.map((file, index) => {
                    var _a;
                    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                            minWidth: 100,
                            height: 100,
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexShrink: 0,
                            backgroundColor: "#f9f9f9",
                        }, children: [((_a = file === null || file === void 0 ? void 0 : file.url) === null || _a === void 0 ? void 0 : _a.startsWith("data:image")) ? ((0, jsx_runtime_1.jsx)("img", { src: file === null || file === void 0 ? void 0 : file.url, alt: `preview-${index}`, style: {
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    filter: file.uploading ? "blur(4px)" : "none",
                                    transition: "filter 0.3s ease",
                                } })) : (getFilePreviewIcon(file.fileType)), file.uploading && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "rgba(255,255,255,0.4)",
                                }, children: (0, jsx_runtime_1.jsx)(material_1.CircularProgress, { size: 24 }) }))] }, index));
                }) })), (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: { position: "relative" }, children: [(0, jsx_runtime_1.jsx)(material_1.TextareaAutosize, { id: "prompt", placeholder: placeholder, value: text, onChange: (e) => setText(e.target.value), onKeyDown: handleKeyDown, disabled: disabled, minRows: 6, maxRows: 12, style: {
                            width: "100%",
                            padding: "12px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontFamily: "inherit",
                            fontSize: "14px",
                            resize: "vertical",
                            overflow: "auto", // works with maxRows
                            boxSizing: "border-box",
                            opacity: disabled ? 0.6 : 1,
                            paddingBottom: "35px",
                            paddingRight: "35px"
                        } }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: "absolute", bottom: 8, left: 8 }, children: (0, jsx_runtime_1.jsx)(material_1.Tooltip, { title: isSelectionMode ? "Disable selection" : "Enable selection", children: (0, jsx_runtime_1.jsx)(material_1.IconButton, { size: "small", onClick: () => setIsSelectionMode(!isSelectionMode), sx: {
                                    backgroundColor: isSelectionMode ? "#9c27b0" : "white",
                                    color: isSelectionMode ? "white" : "#9c27b0",
                                    border: "1px solid #9c27b0",
                                    "&:hover": {
                                        backgroundColor: isSelectionMode ? "#7b1fa2" : "#f3e5f5",
                                    },
                                }, children: (0, jsx_runtime_1.jsx)(Crop_1.default, { fontSize: "small" }) }) }) }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: "absolute", top: 8, right: 8 }, children: (0, jsx_runtime_1.jsxs)(material_1.IconButton, { component: "label", size: "small", disabled: isLoading || disabled, children: [(0, jsx_runtime_1.jsx)(AddPhotoAlternate_1.default, { fontSize: "small" }), (0, jsx_runtime_1.jsx)("input", { type: "file", hidden: true, multiple: true, accept: ".png, .jpeg, .jpg, .ppt, .pptx ,.pdf , .doc, .docx", onChange: handleFileInputChange })] }) }), (session === null || session === void 0 ? void 0 : session.docType) && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: "absolute", bottom: 8, right: 48 }, children: (0, jsx_runtime_1.jsx)(PolishPrompt_1.default, { text: text, onPolish: (newText) => setText(newText), docType: session === null || session === void 0 ? void 0 : session.docType }) })), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: "absolute", bottom: 8, right: 8 }, children: (0, jsx_runtime_1.jsx)(material_1.IconButton, { size: "small", onClick: handleTextSubmit, disabled: isSubmitDisabled, color: "primary", children: isGenerating ? ((0, jsx_runtime_1.jsx)(material_1.CircularProgress, { size: 16 })) : ((0, jsx_runtime_1.jsx)(icons_1.SendIcon, { width: 24, height: 24 })) }) })] })] }));
};
exports.default = ChatInput;
